/* eslint-disable no-underscore-dangle */
import getQuotes from './getQuotes';
import { normalizeScopes } from './quoteScopes';

function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  if (value && typeof value === 'object') {
    const keys = Object.keys(value).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

function normalizeProjectIds(projectIds = []) {
  return [...new Set((projectIds || []).filter(Boolean).map((projectId) => String(projectId)))].sort();
}

function normalizeRequest(scope = {}) {
  // legacy compatibility: if a caller still provides assets+dates, forward unchanged and let getQuotes normalize it.
  if (!Array.isArray(scope?.scopes) && Array.isArray(scope?.assets) && Array.isArray(scope?.dates)) {
    return {
      assets: scope.assets,
      dates: scope.dates,
      projectIds: normalizeProjectIds(scope?.projectIds || []),
    };
  }

  return {
    scopes: normalizeScopes(scope.scopes || []),
    projectIds: normalizeProjectIds(scope.projectIds || []),
  };
}

function mergeRequests(left = {}, right = {}) {
  if (Array.isArray(left?.assets) || Array.isArray(right?.assets)) {
    return normalizeRequest({
      assets: [...(left.assets || []), ...(right.assets || [])],
      dates: [...(left.dates || []), ...(right.dates || [])],
      projectIds: [...(left.projectIds || []), ...(right.projectIds || [])],
    });
  }

  return normalizeRequest({
    scopes: [...(left.scopes || []), ...(right.scopes || [])],
    projectIds: [...(left.projectIds || []), ...(right.projectIds || [])],
  });
}

function createScopeHash(scope = {}) {
  const normalizedScope = normalizeRequest(scope);
  if (Array.isArray(normalizedScope?.assets)) {
    return stableStringify(normalizedScope);
  }
  return stableStringify({
    scopes: normalizedScope.scopes.map((scopeItem) => ({
      asset: scopeItem.asset,
      fromDate: scopeItem.fromDate,
      toDate: scopeItem.toDate,
      requiredDates: scopeItem.requiredDates,
      includeManualQuotes: scopeItem.includeManualQuotes,
    })),
    projectIds: normalizedScope.projectIds,
  });
}

const DEFAULT_DEBOUNCE_MS = 250;

let pendingScope = null;
let pendingWaiters = [];
let pendingTimer = null;
const inFlightByScopeHash = new Map();

function resolveWaiters(waiters, action) {
  waiters.forEach(({ resolve }) => resolve(action));
}

function rejectWaiters(waiters, error) {
  waiters.forEach(({ reject }) => reject(error));
}

function flushPendingQueue(dispatch) {
  if (
    !pendingScope
    || (Array.isArray(pendingScope.scopes) && pendingScope.scopes.length === 0)
    || (Array.isArray(pendingScope.assets) && pendingScope.assets.length === 0)
  ) {
    const waiters = pendingWaiters;
    pendingScope = null;
    pendingWaiters = [];
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }
    resolveWaiters(waiters, null);
    return Promise.resolve(null);
  }

  const scopeToDispatch = pendingScope;
  const waiters = pendingWaiters;
  pendingScope = null;
  pendingWaiters = [];
  if (pendingTimer) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }

  const scopeHash = createScopeHash(scopeToDispatch);
  const existingInFlight = inFlightByScopeHash.get(scopeHash);
  if (existingInFlight) {
    existingInFlight.then(
      (action) => resolveWaiters(waiters, action),
      (error) => rejectWaiters(waiters, error),
    );
    return existingInFlight;
  }

  const requestPromise = Promise.resolve(dispatch(getQuotes(scopeToDispatch)));
  inFlightByScopeHash.set(scopeHash, requestPromise);

  requestPromise
    .then(
      (action) => resolveWaiters(waiters, action),
      (error) => rejectWaiters(waiters, error),
    )
    .finally(() => {
      if (inFlightByScopeHash.get(scopeHash) === requestPromise) {
        inFlightByScopeHash.delete(scopeHash);
      }
    });

  return requestPromise;
}

export function queueQuotesRequest(scope, options = {}) {
  const { dispatch, immediate = false, debounceMs = DEFAULT_DEBOUNCE_MS } = options;
  if (typeof dispatch !== 'function') throw new Error('queueQuotesRequest expects options.dispatch');

  const normalizedScope = normalizeRequest(scope);
  const hasScopes = Array.isArray(normalizedScope.scopes) && normalizedScope.scopes.length > 0;
  const hasLegacyScope = Array.isArray(normalizedScope.assets) && normalizedScope.assets.length > 0 && Array.isArray(normalizedScope.dates) && normalizedScope.dates.length > 0;
  if (!hasScopes && !hasLegacyScope) return Promise.resolve(null);

  const incomingScopeHash = createScopeHash(normalizedScope);
  const existingInFlight = inFlightByScopeHash.get(incomingScopeHash);
  if (existingInFlight) return existingInFlight;

  pendingScope = pendingScope ? mergeRequests(pendingScope, normalizedScope) : normalizedScope;

  const waiterPromise = new Promise((resolve, reject) => {
    pendingWaiters.push({ resolve, reject });
  });

  if (immediate) {
    flushPendingQueue(dispatch);
  } else if (!pendingTimer) {
    pendingTimer = setTimeout(() => {
      flushPendingQueue(dispatch);
    }, debounceMs);
  }

  return waiterPromise;
}

export function flushQuotesRequestQueue(options = {}) {
  const { dispatch } = options;
  if (typeof dispatch !== 'function') throw new Error('flushQuotesRequestQueue expects options.dispatch');
  return flushPendingQueue(dispatch);
}

export function __resetQuotesRequestPlannerForTests() {
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingScope = null;
  pendingWaiters = [];
  pendingTimer = null;
  inFlightByScopeHash.clear();
}
